import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import { muiColors } from "../theme/designSystem";
import "../theme/designSystemVariables.css";
import { useTranslation } from "react-i18next";
import { applyStoredThemeMode, useStoredTheme } from "@/utils/theme-context";
import { cacheApi } from "@/services/cache-service";
import { useAuth } from "@/contexts/AuthContext";

const normalizeThemeMode = (themeMode) =>
  themeMode === "dark" || themeMode === "light" ? themeMode : null;

const DEFAULT_TAB_NAME = "bunyan";
const DEFAULT_FAVICON = "/assets/favicon.ico";

// Create context
const ThemeContext = createContext({
  mode: "dark",
  setThemeMode: () => {},
  toggleTheme: () => {},
});

// Custom hook to use the theme context
export const useThemeMode = () => useContext(ThemeContext);

// Theme provider component
export const ThemeProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const { cache } = useAuth();
  const storedTheme = useStoredTheme();
  const cachedThemeMode = normalizeThemeMode(cache?.preferences?.theme_mode);
  const [mode, setMode] = useState(() => {
    const savedMode = normalizeThemeMode(localStorage.getItem("themeMode"));
    return cachedThemeMode || savedMode || "dark";
  });

  const setThemeMode = useCallback((themeMode) => {
    const nextMode = normalizeThemeMode(themeMode);
    if (!nextMode) return;

    setMode(nextMode);
  }, []);

  const toggleTheme = useCallback(
    (event) => {
      const selectedMode =
        typeof event?.target?.checked === "boolean"
          ? event.target.checked
            ? "dark"
            : "light"
          : mode === "light"
            ? "dark"
            : "light";

      setThemeMode(selectedMode);
      cacheApi.updatePreferences({ theme_mode: selectedMode }).catch((err) => {
        console.error("Failed to update theme preference:", err);
      });
    },
    [mode, setThemeMode],
  );

  useEffect(() => {
    if (cachedThemeMode) {
      setThemeMode(cachedThemeMode);
    }
  }, [cachedThemeMode, setThemeMode]);

  useEffect(() => {
    // Make sure document class matches current theme
    localStorage.setItem("themeMode", mode);
    document.documentElement.classList.remove("light-theme", "dark-theme");
    document.documentElement.classList.add(`${mode}-theme`);
    const root = document.documentElement;
    if (mode === "light") root.classList.remove("dark");
    else root.classList.add("dark");
    applyStoredThemeMode(storedTheme, mode);
  }, [mode, storedTheme]);

  useEffect(() => {
    document.title = storedTheme.tabName?.trim() || DEFAULT_TAB_NAME;

    let favicon = document.querySelector('link[rel~="icon"]');
    if (!favicon) {
      favicon = document.createElement("link");
      favicon.rel = "icon";
      document.head.appendChild(favicon);
    }

    favicon.href = storedTheme.favicon?.[mode] || DEFAULT_FAVICON;
  }, [mode, storedTheme.favicon, storedTheme.tabName]);

  // Create base theme configurations
  const getTheme = (mode, direction) => {
    return createTheme({
      direction,
      palette: {
        mode,
        ...(mode === "light"
          ? {
              // Light theme using design system colors
              primary: {
                main:
                  storedTheme.button.primary.backgroundColor[mode] ||
                  muiColors.primary.lightMain, // #13D39E
                light: muiColors.primary.light, // #B8F2E2
                dark: muiColors.primary.dark, // #043225
                contrastText: muiColors.primary.contrastText,
              },
              secondary: muiColors.secondary,
              background: {
                default:
                  storedTheme.general.backgroundColor[mode] ||
                  muiColors.background.default, // #F1F5F9
                paper:
                  storedTheme.card.backgroundColor[mode] ||
                  muiColors.background.paper, // #FFFFFF
              },
              text: {
                primary:
                  storedTheme.general.textColor[mode] || muiColors.text.primary, // #2E3334
                secondary: muiColors.text.secondary, // #94A3B8
              },
              error: muiColors.error,
              warning: muiColors.warning,
              info: muiColors.info,
              success: muiColors.success,
              grey: muiColors.grey,
              divider: muiColors.divider, // #e2e8f0
            }
          : {
              // Dark theme using design system colors
              primary: {
                main:
                  storedTheme.button.primary.backgroundColor[mode] ||
                  muiColors.primary.main, // #13D39E
                light: muiColors.primary.light, // #B8F2E2
                dark: muiColors.primary.dark, // #043225
                contrastText: muiColors.primary.contrastText,
              },
              secondary: muiColors.secondary,
              background: {
                default:
                  storedTheme.general.backgroundColor[mode] ||
                  muiColors.background.defaultDark, // #2E3334
                paper:
                  storedTheme.card.backgroundColor[mode] ||
                  muiColors.background.paperDark, // #141A1B
              },
              text: {
                primary:
                  storedTheme.general.textColor[mode] ||
                  muiColors.text.primaryDark, // #F9F9F9
                secondary: muiColors.text.secondaryDark, // #94A3B8
              },
              error: muiColors.error,
              warning: muiColors.warning,
              info: muiColors.info,
              success: muiColors.success,
              grey: muiColors.grey,
              divider: muiColors.dividerDark, // rgba(255, 255, 255, 0.12)
            }),
      },
      typography: {
        fontFamily:
          direction === "rtl"
            ? '"IBM Plex Sans Arabic", "Noto Sans Arabic", "Segoe UI", Tahoma, Arial, "IBM Plex Sans", sans-serif'
            : '"IBM Plex Sans", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
          fontWeight: 700,
        },
        h2: {
          fontWeight: 700,
        },
        h3: {
          fontWeight: 600,
        },
        h4: {
          fontWeight: 600,
        },
        h5: {
          fontWeight: 600,
        },
        h6: {
          fontWeight: 600,
        },
        button: {
          fontWeight: 600,
          textTransform: "none",
        },
      },
      shape: {
        borderRadius: 10,
      },
      components: {
        MuiFormControlLabel: {
          styleOverrides: {
            root: {
              marginInlineStart: "-11px",
              marginInlineEnd: "16px",
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              padding: "6px 16px",
              fontSize: "14px",
              boxShadow: "none",
              minWidth: "64px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: "none",
              },
              "& .MuiSvgIcon-root": {
                color: "inherit",
              },
              "& .MuiTypography-root": {
                color: "inherit",
                fontWeight: "inherit",
              },
              "& span": {
                color: "inherit",
                fontSize: "inherit",
                fontWeight: "inherit",
              },
              "& .MuiButton-startIcon": {
                marginInlineEnd: "8px",
              },
              "& .MuiButton-endIcon": {
                marginInlineStart: "8px",
              },
              "&.icon-only": {
                minWidth: "auto",
              },
            },
            // PRIMARY CONTAINED BUTTON - Using design system variables
            containedPrimary: {
              backgroundColor: storedTheme.button.primary.backgroundColor[mode],
              color: storedTheme.button.primary.textColor[mode],
              border: "1px solid",
              borderColor: storedTheme.button.primary.borderColor[mode],
              "&:hover": {
                color: storedTheme.button.primary.hoverTextColor[mode],
                backgroundColor:
                  storedTheme.button.primary.hoverBackgroundColor[mode],
                borderColor: storedTheme.button.primary.hoverBorderColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.primary.hoverBackgroundColor[mode],
                borderColor: storedTheme.button.primary.hoverBorderColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            // PRIMARY OUTLINED BUTTON - Using design system variables
            outlinedPrimary: {
              backgroundColor: "transparent",
              color: storedTheme.button.primary.backgroundColor[mode],
              border: "1px solid",
              borderColor: storedTheme.button.primary.borderColor[mode],
              "&:hover": {
                backgroundColor:
                  storedTheme.button.primary.hoverBackgroundColor[mode],
                borderColor: storedTheme.button.primary.hoverBorderColor[mode],
                color: storedTheme.button.primary.hoverTextColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.primary.hoverBackgroundColor[mode],
                color: storedTheme.button.primary.hoverTextColor[mode],
                borderColor: storedTheme.button.primary.hoverBorderColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            // SECONDARY CONTAINED BUTTON - Using design system variables
            containedSecondary: {
              backgroundColor:
                storedTheme.button.secondary.backgroundColor[mode],
              color: storedTheme.button.secondary.textColor[mode],
              border: "1px solid",
              borderColor: storedTheme.button.secondary.borderColor[mode],
              "&:hover": {
                color: storedTheme.button.secondary.hoverTextColor[mode],
                backgroundColor:
                  storedTheme.button.secondary.hoverBackgroundColor[mode],
                borderColor:
                  storedTheme.button.secondary.hoverBorderColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.secondary.hoverBackgroundColor[mode],
                borderColor:
                  storedTheme.button.secondary.hoverBorderColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            // SECONDARY OUTLINED BUTTON - Using design system variables
            outlinedSecondary: {
              backgroundColor: "transparent",
              color: storedTheme.button.secondary.backgroundColor[mode],
              border: "1px solid",
              borderColor: storedTheme.button.secondary.borderColor[mode],
              "&:hover": {
                backgroundColor:
                  storedTheme.button.secondary.hoverBackgroundColor[mode],
                borderColor:
                  storedTheme.button.secondary.hoverBorderColor[mode],
                color: storedTheme.button.secondary.hoverTextColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.secondary.hoverBackgroundColor[mode],
                color: storedTheme.button.secondary.hoverTextColor[mode],
                borderColor:
                  storedTheme.button.secondary.hoverBorderColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            // SUCCESS CONTAINED BUTTON - Using design system variables
            containedSuccess: {
              backgroundColor: storedTheme.button.success.backgroundColor[mode],
              color: storedTheme.button.success.textColor[mode],
              border: "1px solid",
              borderColor: storedTheme.button.success.borderColor[mode],
              "&:hover": {
                color: storedTheme.button.success.hoverTextColor[mode],
                backgroundColor:
                  storedTheme.button.success.hoverBackgroundColor[mode],
                borderColor: storedTheme.button.success.hoverBorderColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.success.hoverBackgroundColor[mode],
                borderColor: storedTheme.button.success.hoverBorderColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            // SUCCESS OUTLINED BUTTON - Using design system variables
            outlinedSuccess: {
              backgroundColor: "transparent",
              color: storedTheme.button.success.backgroundColor[mode],
              border: "1px solid",
              borderColor: storedTheme.button.success.borderColor[mode],
              "&:hover": {
                backgroundColor:
                  storedTheme.button.success.hoverBackgroundColor[mode],
                borderColor: storedTheme.button.success.hoverBorderColor[mode],
                color: storedTheme.button.success.hoverTextColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.success.hoverBackgroundColor[mode],
                color: storedTheme.button.success.hoverTextColor[mode],
                borderColor: storedTheme.button.success.hoverBorderColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            // ERROR CONTAINED BUTTON - Using design system variables
            containedError: {
              backgroundColor: storedTheme.button.error.backgroundColor[mode],
              color: storedTheme.button.error.textColor[mode],
              border: "1px solid",
              borderColor: storedTheme.button.error.borderColor[mode],
              "&:hover": {
                color: storedTheme.button.error.hoverTextColor[mode],
                backgroundColor:
                  storedTheme.button.error.hoverBackgroundColor[mode],
                borderColor: storedTheme.button.error.hoverBorderColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.error.hoverBackgroundColor[mode],
                borderColor: storedTheme.button.error.hoverBorderColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            // ERROR OUTLINED BUTTON - Using design system variables
            outlinedError: {
              backgroundColor: "transparent",
              color: storedTheme.button.error.backgroundColor[mode],
              border: "1px solid",
              borderColor: storedTheme.button.error.borderColor[mode],
              "&:hover": {
                backgroundColor:
                  storedTheme.button.error.hoverBackgroundColor[mode],
                borderColor: storedTheme.button.error.hoverBorderColor[mode],
                color: storedTheme.button.error.hoverTextColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.error.hoverBackgroundColor[mode],
                color: storedTheme.button.error.hoverTextColor[mode],
                borderColor: storedTheme.button.error.hoverBorderColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            // TEXT BUTTONS - Using design system variables
            text: {
              "&:hover": {
                backgroundColor:
                  storedTheme.button.primary.hoverBackgroundColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.primary.hoverBackgroundColor[mode],
                transform: "translateY(0px)",
              },
            },
            textPrimary: {
              color: storedTheme.button.primary.backgroundColor[mode],
              "&:hover": {
                backgroundColor:
                  storedTheme.button.primary.hoverBackgroundColor[mode],
                color: storedTheme.button.primary.hoverTextColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.primary.hoverBackgroundColor[mode],
                color: storedTheme.button.primary.hoverTextColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            textSecondary: {
              color: storedTheme.button.secondary.backgroundColor[mode],
              "&:hover": {
                backgroundColor:
                  storedTheme.button.secondary.hoverBackgroundColor[mode],
                color: storedTheme.button.secondary.hoverTextColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.secondary.hoverBackgroundColor[mode],
                color: storedTheme.button.secondary.hoverTextColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            textSuccess: {
              color: storedTheme.button.success.backgroundColor[mode],
              "&:hover": {
                backgroundColor:
                  storedTheme.button.success.hoverBackgroundColor[mode],
                color: storedTheme.button.success.hoverTextColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.success.hoverBackgroundColor[mode],
                color: storedTheme.button.success.hoverTextColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
            textError: {
              color: storedTheme.button.error.backgroundColor[mode],
              "&:hover": {
                backgroundColor:
                  storedTheme.button.error.hoverBackgroundColor[mode],
                color: storedTheme.button.error.hoverTextColor[mode],
                transform: "translateY(-1px)",
              },
              "&:active": {
                backgroundColor:
                  storedTheme.button.error.hoverBackgroundColor[mode],
                color: storedTheme.button.error.hoverTextColor[mode],
                transform: "translateY(0px)",
              },
              "&.Mui-disabled": {
                opacity: 0.7,
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow:
                mode === "light" ? "0px 2px 8px rgba(0, 0, 0, 0.03)" : "none",
              backgroundColor: storedTheme.card.backgroundColor[mode],
              border: "1px solid",
              borderColor: storedTheme.card.borderColor[mode],
              borderRadius: storedTheme.card.borderRadius[mode],
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: "none",
              backgroundColor: storedTheme.card.backgroundColor[mode],
              border: "1px solid",
              borderColor: storedTheme.card.borderColor[mode],
              borderRadius: storedTheme.card.borderRadius[mode],
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              border: "none",
              backgroundColor: storedTheme.header.backgroundColor[mode],
              borderBottom: `1px solid ${
                mode === "light" ? muiColors.divider : muiColors.dividerDark
              }`,
              boxShadow:
                mode === "light" ? "0px 2px 8px rgba(0, 0, 0, 0.03)" : "none",
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              borderRadius: 0,
              backgroundColor: storedTheme.sidebar.backgroundColor[mode],
              border: "none",
            },
          },
        },
        MuiSvgIcon: {
          styleOverrides: {
            root: {
              color: storedTheme.button.primary.backgroundColor[mode], // default icon color
              "&:hover": {
                color: "inherit", // Keep consistent color on hover
              },
            },
            colorPrimary: {
              color: "inherit",
              "&:hover": {
                color: "inherit",
              },
            },
            colorAction: {
              color: "inherit",
              "&:hover": {
                color: "inherit",
              },
            },
            colorError: {
              color: "inherit",
              "&:hover": {
                color: "inherit",
              },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              "&.icon-btn-primary": {
                backgroundColor: "#043225",
                color: "#043225",
                display: "flex",
                width: "48px",
                height: "34px",
                padding: "4px 12px",
                justifyContent: "center",
                alignItems: "center",
                flexShrink: 0,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#0a916c",
                  color: "#0a916c",
                },
                "@media (max-width: 600px)": {
                  display: "inline-flex",
                  padding: "8px 16px",
                  justifyContent: "center",
                  alignItems: "center",
                },
              },
              "&.icon-btn-secondary": {
                backgroundColor: "#2e3334",
                color: "#b7bbbe",
                display: "flex",
                width: "34px",
                height: "34px",
                padding: "4px",
                justifyContent: "center",
                alignItems: "center",
                flexShrink: 0,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#4a5355",
                  color: "#f1f5f9",
                },
                "@media (max-width: 600px)": {
                  display: "inline-flex",
                  padding: "8px",
                  justifyContent: "center",
                  alignItems: "center",
                },
              },
              "&.icon-btn-ghost": {
                backgroundColor: "#000000",
                color: "#000000",
                display: "flex",
                width: "34px",
                height: "34px",
                padding: "4px",
                justifyContent: "center",
                alignItems: "center",
                flexShrink: 0,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#090f10",
                  color: "#f1f5f9",
                },
                "@media (max-width: 600px)": {
                  display: "inline-flex",
                  padding: "8px",
                  justifyContent: "center",
                  alignItems: "center",
                },
              },
              "&.icon-btn-destructive": {
                backgroundColor: "#451b1b",
                color: "#c78b8c",
                display: "flex",
                width: "34px",
                height: "34px",
                padding: "4px",
                justifyContent: "center",
                alignItems: "center",
                flexShrink: 0,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: "#8e1e1e",
                  color: "#ffbdbd",
                },
                "@media (max-width: 600px)": {
                  display: "inline-flex",
                  padding: "8px",
                  justifyContent: "center",
                  alignItems: "center",
                },
              },
              "&.icon-btn-floating": {
                backgroundColor: "#2e3334",
                color: "#f1f5f9",
                display: "flex",
                width: "34px",
                height: "34px",
                padding: "4px",
                justifyContent: "center",
                alignItems: "center",
                flexShrink: 0,
                borderRadius: "99px",
                "&:hover": {
                  backgroundColor: "#4a5355",
                  color: "#f1f5f9",
                },
                "@media (max-width: 600px)": {
                  display: "inline-flex",
                  padding: "8px",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "99px",
                },
              },
            },
          },
        },
        MuiAlert: {
          styleOverrides: {
            root: {
              borderRadius: 8,
            },
            standardInfo: {
              backgroundColor: mode === "light" ? "#EFF6FF" : "#021030",
              color: mode === "light" ? "#3182CE" : undefined,
              "& .MuiAlert-icon": {
                color: mode === "light" ? "#3182CE" : undefined,
              },
              border:
                mode === "light" ? "1px solid #DBEAFE" : "1px solid #92b2ff73",
              borderRadius: "8px",
            },
            standardError: {
              backgroundColor: mode === "light" ? "#FEF2F2" : "#7f1d1d",
              color: mode === "light" ? "#E53E3E" : undefined,
              "& .MuiAlert-icon": {
                color: mode === "light" ? "#E53E3E" : undefined,
              },
              border: mode === "light" ? "1px solid #FEE2E2" : "none",
            },
            standardWarning: {
              backgroundColor: mode === "light" ? "#FEF5ED" : "#78350f",
              color: mode === "light" ? "#DD6B20" : undefined,
              "& .MuiAlert-icon": {
                color: mode === "light" ? "#DD6B20" : undefined,
              },
              border: mode === "light" ? "1px solid #FDEDD3" : "none",
            },
            standardSuccess: {
              backgroundColor: mode === "light" ? "#ECFDF5" : "#064e3b",
              color: mode === "light" ? "#0FA97E" : undefined,
              "& .MuiAlert-icon": {
                color: mode === "light" ? "#0FA97E" : undefined,
              },
              border: mode === "light" ? "1px solid #D1FAE5" : "none",
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 6,
            },
            colorPrimary: {
              backgroundColor: mode === "light" ? "#ECFDF5" : "#021A13",
              color: mode === "light" ? "#0FA97E" : "#0FA97E",
              borderColor: mode === "light" ? "#D1FAE5" : "#0FA97E",
            },
            colorSuccess: {
              backgroundColor: mode === "light" ? "#ECFDF5" : "#021A13",
              color: mode === "light" ? "#0FA97E" : "#0FA97E",
              borderColor: mode === "light" ? "#D1FAE5" : "#0FA97E",
            },
            colorError: {
              backgroundColor: mode === "light" ? "#FEF2F2" : "#260000",
              color: mode === "light" ? "#E53E3E" : "#FF341E",
              borderColor: mode === "light" ? "#FEE2E2" : "#FF341E",
            },
            colorWarning: {
              backgroundColor: mode === "light" ? "#FEF5ED" : "#261700",
              color: mode === "light" ? "#DD6B20" : "#DF8A01",
              borderColor: mode === "light" ? "#FDEDD3" : "#DF8A01",
            },
            colorInfo: {
              backgroundColor: mode === "light" ? "#EFF6FF" : "#021030",
              color: mode === "light" ? "#3182CE" : "#92B2FF",
              borderColor: mode === "light" ? "#DBEAFE" : "#92B2FF",
            },
          },
        },
        MuiListItem: {
          styleOverrides: {
            root: {
              "&.Mui-selected": {
                backgroundColor:
                  storedTheme.sidebar.activeLinkBackgroundColor[mode],
                "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
                  color: storedTheme.sidebar.activeLinkTextColor[mode],
                },
                "&:hover": {
                  backgroundColor:
                    storedTheme.sidebar.hoverBackgroundColor[mode],
                  "& .MuiListItemIcon-root": {
                    color: storedTheme.sidebar.hoverTextColor[mode],
                  },
                  "& .MuiListItemText-primary": {
                    color: storedTheme.sidebar.hoverTextColor[mode],
                  },
                  "& .MuiSvgIcon-root": {
                    color: storedTheme.sidebar.hoverTextColor[mode],
                  },
                },
              },
            },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              borderColor: mode === "light" ? "#E2E8F0" : "#2e3334",
            },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: {
              color: mode === "light" ? "#94A3B8" : "#94A3B8",

              "&.Mui-error": {
                color: "#8E1E1E",
              },

              "&.Mui-disabled": {
                color: "#626365",
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            select: {
              padding: "8px 12px",
            },
            root: {
              color: storedTheme.input.textColor[mode],
              backgroundColor: storedTheme.input.backgroundColor[mode],
              borderRadius: storedTheme.input.borderRadius[mode],

              "& .MuiSelect-select": {
                color: storedTheme.input.textColor[mode],
              },

              "& .MuiSvgIcon-root": {
                color: storedTheme.input.textColor[mode],
              },
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              "& .MuiInputBase-input": {
                padding: "8px 12px",
              },
              // Filled variant styles
              "& .MuiFilledInput-root": {
                color: storedTheme.input.textColor[mode],
                backgroundColor: storedTheme.input.backgroundColor[mode],
                borderRadius: `${storedTheme.input.borderRadius[mode]} ${storedTheme.input.borderRadius[mode]} 0 0`,
                "&:hover:not(.Mui-disabled):before": {
                  borderBottomColor: storedTheme.input.borderColor[mode],
                },
                "&:after": {
                  borderBottomColor: storedTheme.input.borderColor[mode],
                  borderBottomWidth: "2px",
                },
                "&.Mui-focused": {
                  backgroundColor: storedTheme.input.backgroundColor[mode],
                  "&:after": {
                    borderBottomColor: storedTheme.input.borderColor[mode],
                  },
                },
                "& input": {
                  color: storedTheme.input.textColor[mode],
                  caretColor: storedTheme.input.textColor[mode],
                },
                "& textarea": {
                  color: storedTheme.input.textColor[mode],
                  caretColor: storedTheme.input.textColor[mode],
                },
              },
              // Outlined variant styles
              "& .MuiOutlinedInput-root": {
                color: storedTheme.input.textColor[mode],
                backgroundColor: storedTheme.input.backgroundColor[mode],
                borderRadius: storedTheme.input.borderRadius[mode],
                "&:hover fieldset": {
                  borderColor: storedTheme.input.borderColor[mode],
                },
                "&.Mui-focused fieldset": {
                  borderColor: storedTheme.input.borderColor[mode],
                  borderWidth: "2px",
                },
                "& input": {
                  color: storedTheme.input.textColor[mode],
                  caretColor: storedTheme.input.textColor[mode],
                },
                "& textarea": {
                  color: storedTheme.input.textColor[mode],
                  caretColor: storedTheme.input.textColor[mode],
                },
              },
              // Standard variant styles
              "& .MuiInput-root": {
                color: storedTheme.input.textColor[mode],
                backgroundColor: "transparent",
                borderRadius: storedTheme.input.borderRadius[mode],
                "&:hover:not(.Mui-disabled):before": {
                  borderBottomColor: storedTheme.input.borderColor[mode],
                },
                "&:after": {
                  borderBottomColor: storedTheme.input.borderColor[mode],
                },
                "&.Mui-focused fieldset": {
                  borderColor: storedTheme.input.borderColor[mode],
                  borderWidth: "2px",
                },
                "& input": {
                  color: storedTheme.input.textColor[mode],
                  caretColor: storedTheme.input.textColor[mode],
                },
                "& textarea": {
                  color: storedTheme.input.textColor[mode],
                  caretColor: storedTheme.input.textColor[mode],
                },
              },
              "& .MuiInputLabel-root": {
                color: storedTheme.input.textColor[mode],
              },
              // Helper text styles
              "& .MuiFormHelperText-root": {
                color: mode === "light" ? "#94A3B8" : "#94A3B8",
                "&.Mui-error": {
                  color: "#8E1E1E",
                },
                "&.Mui-disabled": {
                  color: "#626365",
                },
              },
              // Error icon styles
              "& .MuiInputAdornment-root": {
                "& .MuiSvgIcon-root": {
                  color: mode === "light" ? "#2E3334" : "#F9F9F9",
                  "&.error-icon": {
                    color: "#78120C",
                  },
                },
                "&.Mui-error .MuiSvgIcon-root": {
                  color: "#8E1E1E",
                },
                "&.Mui-disabled .MuiSvgIcon-root": {
                  color: "#626365",
                },
              },
              // Specific error icon positioning
              "& .MuiInputBase-root.Mui-error:not(.has-trailing-icon)": {
                paddingInlineEnd: "48px",
                "&::after": {
                  content: '"error"',
                  position: "absolute",
                  insetInlineEnd: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontFamily: "Material Icons",
                  fontSize: "20px",
                  color: "#78120C",
                  pointerEvents: "none",
                },
              },
              // Date picker specific error styles
              "& .MuiInputBase-root.Mui-error .MuiInputAdornment-root .MuiSvgIcon-root":
                {
                  color: "#8E1E1E !important",
                },
              "& .MuiInputBase-root.Mui-error .MuiIconButton-root .MuiSvgIcon-root":
                {
                  color: "#8E1E1E !important",
                },
              // Date picker calendar icon in error state
              "& .Mui-error .MuiInputAdornment-positionEnd .MuiSvgIcon-root": {
                color: "#8E1E1E !important",
              },
              "& .Mui-error .MuiIconButton-root .MuiSvgIcon-root": {
                color: "#8E1E1E !important",
              },
            },
          },
        },
        // Additional date picker component styling
        MuiDatePicker: {
          styleOverrides: {
            root: {
              "& .MuiInputBase-root.Mui-error": {
                "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                  color: "#8E1E1E !important",
                },
                "& .MuiIconButton-root .MuiSvgIcon-root": {
                  color: "#8E1E1E !important",
                },
              },
            },
          },
        },
        MuiDesktopDatePicker: {
          styleOverrides: {
            root: {
              "& .MuiInputBase-root.Mui-error": {
                "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                  color: "#8E1E1E !important",
                },
                "& .MuiIconButton-root .MuiSvgIcon-root": {
                  color: "#8E1E1E !important",
                },
              },
            },
          },
        },
        MuiMobileDatePicker: {
          styleOverrides: {
            root: {
              "& .MuiInputBase-root.Mui-error": {
                "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                  color: "#8E1E1E !important",
                },
                "& .MuiIconButton-root .MuiSvgIcon-root": {
                  color: "#8E1E1E !important",
                },
              },
            },
          },
        },
        MuiRadio: {
          styleOverrides: {
            root: {
              color: storedTheme.radiobutton.backgroundColor[mode],
              "&.Mui-checked": {
                color: storedTheme.radiobutton.backgroundColor[mode],
              },
              "&.Mui-checked .MuiSvgIcon-root": {
                color: storedTheme.radiobutton.backgroundColor[mode],
              },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              color: storedTheme.checkBox.backgroundColor[mode],
              "&.Mui-checked": {
                color: storedTheme.checkBox.backgroundColor[mode],
              },
              "&.Mui-checked .MuiSvgIcon-root": {
                color: storedTheme.checkBox.backgroundColor[mode],
              },
            },
          },
        },
        MuiSlider: {
          styleOverrides: {
            root: {
              color: storedTheme.slider.backgroundColor[mode],
              "& .MuiSlider-thumb": {
                backgroundColor: storedTheme.slider.backgroundColor[mode],
                border: `2px solid ${storedTheme.slider.backgroundColor[mode]}`,
                "&:hover": {
                  boxShadow: "0 0 0 6px rgba(0,0,0,0.1)",
                },
              },
              "& .MuiSlider-track": {
                backgroundColor: storedTheme.slider.backgroundColor[mode],
              },
              "& .MuiSlider-rail": {
                opacity: 0.3,
                backgroundColor: storedTheme.slider.backgroundColor[mode],
              },
              "& .MuiSlider-valueLabel": {
                backgroundColor: storedTheme.slider.backgroundColor[mode],
              },
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            switchBase: () => {
              const bg = storedTheme.inputSwitch.backgroundColor[mode];
              return {
                "&.Mui-checked": {
                  color: bg,
                  "& + .MuiSwitch-track": {
                    backgroundColor: bg,
                    opacity: 0.5,
                  },
                },
                "&.Mui-checked:hover": {
                  backgroundColor: "rgba(0,0,0,0.08)",
                },
              };
            },
            track: {
              opacity: 0.3,
            },
          },
        },
        MuiStepIcon: {
          styleOverrides: {
            root: {
              "&.Mui-active": {
                color: storedTheme.button.primary.backgroundColor[mode], // selected step
              },
            },
          },
        },
      },
    });
  };

  // Create the theme based on current mode
  const theme = useMemo(() => {
    const direction = i18n.dir() === "rtl" ? "rtl" : "ltr";
    return getTheme(mode, direction);
  }, [storedTheme, mode, i18n.language]);

  // Context value
  const contextValue = useMemo(
    () => ({
      mode,
      setThemeMode,
      toggleTheme,
    }),
    [mode, setThemeMode, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
